import { Map, TileLayer as TileLayerEntity } from 'leaflet';
import { useEffect, useRef, useState } from 'react';
import Control from 'react-leaflet-custom-control';
import { useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { uniqueId } from 'src/lib/dayjs/unique-id';
import { CheckBoxFloodRiskComponent } from 'src/modules/map-geo-jsons/components/CheckBoxFloodRisk.component';
import { CheckBoxPrecipitationLayer } from 'src/modules/map-layers/components/CheckBoxPrecipitationLayer.component';
import { CheckBoxRegionGeoJSON } from 'src/modules/map-layers/components/CheckBoxRegionGeoJSON.component';
import { CheckBoxRiverNetworkComponent } from 'src/modules/map-layers/components/CheckBoxRiverNetwork.component';
import { SelectBoxBaseLayer } from 'src/modules/map-layers/components/SelectBoxBaseLayer.component';
import { CheckBoxWaterLevelMarkers } from 'src/modules/map-markers/components/CheckBoxWaterLevel.component';
import { MapWeatherInfo } from 'src/modules/map-weather-info/components/MapWeatherInfo.component';
import { compactMapMediaQuery, useMobileViewport } from 'src/modules/map/hooks/useMobileViewport';
import { setControlBottomClassName } from 'src/redux/slices/precipitationPlaying.slice';
import { twMerge } from 'tailwind-merge';
import { useAuth } from '../../../lib/auth/use-auth';
import { isAdminAccount } from 'src/lib/auth/roles';

export function TileLayerControlTopRight({
  baseTileLayer,
  map,
}: {
  baseTileLayer: React.RefObject<TileLayerEntity>;
  map: React.RefObject<Map>;
}) {
  const { user, isAuthenticated } = useAuth();
  const id = useRef(uniqueId('tile-layer-control-top-right'));
  const [selectedAreaName, setSelectedAreaName] = useState<string>();
  const isCompactViewport = useMobileViewport();
  const [isMenuOpen, setIsMenuOpen] = useState(() => !window.matchMedia(compactMapMediaQuery).matches);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    setIsMenuOpen(!isCompactViewport);
  }, [isCompactViewport]);

  useEffect(() => {
    document.body.classList.toggle('map-right-menu-open', isMenuOpen);
    const menuContainer = document.getElementById(id.current);
    if (menuContainer) {
      menuContainer.className = twMerge(menuContainer.className, isMenuOpen ? 'w-60' : 'w-0');
    }
    dispatch(setControlBottomClassName(isMenuOpen ? 'w-[calc(100vw-15rem)]' : 'w-screen'));
    window.requestAnimationFrame(() => {
      map.current?.invalidateSize();
      window.setTimeout(() => map.current?.invalidateSize(), 300);
    });
  }, [dispatch, isMenuOpen, map]);

  useEffect(() => {
    return () => document.body.classList.remove('map-right-menu-open');
  }, []);

  const isDeviceMonitorViewable = Boolean(user?.role);

  return (
    <Control
      position="topright"
      container={{
        id: id.current,
        className: twMerge(
          'h-[100dvh] overflow-visible bg-transparent transition-[width] duration-300',
          isMenuOpen ? 'w-60' : 'w-0',
        ),
      }}>
      <div
        className={twMerge(
          'flex h-[100dvh] w-60 min-w-[15rem] flex-col gap-1 overflow-y-auto bg-white p-3 transition-opacity md:gap-4',
          isMenuOpen ? 'visible opacity-100' : 'invisible opacity-0',
        )}
        aria-hidden={!isMenuOpen}>
        <SelectBoxBaseLayer baseTileLayer={baseTileLayer} />

        <CheckBoxPrecipitationLayer baseTileLayer={baseTileLayer} />
        {/* <CheckBoxElevationLayer baseTileLayer={baseTileLayer} /> */}
        <CheckBoxRiverNetworkComponent baseTileLayer={baseTileLayer} />

        <CheckBoxRegionGeoJSON baseTileLayer={baseTileLayer} baseMap={map} checked />

        <div className="flex flex-wrap gap-3 border border-dashed border-gray-500 p-2 md:flex-col md:flex-nowrap md:p-4">
          <CheckBoxWaterLevelMarkers baseTileLayer={baseTileLayer} />
          {/* TODO: turn off b/c request customer */}
          {/* <CheckBoxFloodRiskComponent baseTileLayer={baseTileLayer} /> */}
        </div>

        <MapWeatherInfo
          onSelectArea={(area) => {
            setSelectedAreaName(area?.AREA_NAME.S);
          }}
        />

        {selectedAreaName && (
          <Link
            className="mb-2 mt-auto border-2 border-blue-500 p-1 text-center text-xl text-blue-500"
            to={`/flow?areaName=${selectedAreaName}`}>
            概要図表示
          </Link>
        )}
      {
          isDeviceMonitorViewable && (
              <Link
                  className="mb-2 border-2 border-blue-500 p-1 text-center text-xl text-blue-500"
                  to={`/device-monitor`}>
                  デバイスモニター
              </Link>
          )
      }
      {isAuthenticated && isAdminAccount(user) && (
        <Link
          className="mb-2 border-2 border-blue-500 p-1 text-center text-xl text-blue-500"
          to="/admin/devices">
          管理者画面
        </Link>
      )}
      <Link
        className="mb-2 mt-auto border-2 border-blue-500 p-1 text-center text-xl text-blue-500"
        to={`/mizusaki`}>
        地図上水先を表示
      </Link>


        {/* logout */}
        <button
          type="button"
          className="flex items-center justify-center text-sm font-normal"
          onClick={() => {
            navigate('/logout');
          }}>
          <span className="ml-3 whitespace-nowrap md:inline-block">ログアウト</span>
        </button>
      </div>

      <ButtonEnableControlTopRight
        isOpen={isMenuOpen}
        controlsId={id.current}
        onClick={() => setIsMenuOpen((current) => !current)}
      />
    </Control>
  );
}

export function ButtonEnableControlTopRight({
  isOpen,
  controlsId,
  onClick,
}: {
  isOpen: boolean;
  controlsId: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`absolute z-[1100] grid h-11 w-11 place-items-center rounded-md border border-blue-900 bg-white shadow-md transition-[right,transform] duration-300 hover:scale-105 ${
        isOpen ? 'right-[15rem]' : 'right-2'
      }`}
      style={{ top: 'max(0.5rem, env(safe-area-inset-top))' }}
      aria-controls={controlsId}
      aria-expanded={isOpen}
      aria-label={isOpen ? '右メニューを閉じる' : '右メニューを開く'}
      onClick={onClick}>
      <svg
        className="h-6 w-6 text-black"
        viewBox="0 0 24 24"
        strokeWidth="2"
        stroke="currentColor"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round">
        <path stroke="none" d="M0 0h24v24H0z" />
        {isOpen ? (
          <>
            <polyline points="7 7 12 12 7 17" />
            <polyline points="13 7 18 12 13 17" />
          </>
        ) : (
          <>
            <polyline points="11 7 6 12 11 17" />
            <polyline points="17 7 12 12 17 17" />
          </>
        )}
      </svg>
    </button>
  );
}
